import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmergencyServiceCard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const emergencyServices = [
    {
      id: 1,
      type: "Network Outage",
      responseTime: "1-2 hours",
      availability: "24/7",
      icon: "Wifi",
      description: "Complete network failure or critical connectivity issues"
    },
    {
      id: 2,
      type: "Fiber Cut/Damage",
      responseTime: "2-4 hours",
      availability: "24/7",
      icon: "AlertTriangle",
      description: "Physical damage to fiber optic cables requiring immediate repair"
    },
    {
      id: 3,
      type: "Equipment Failure",
      responseTime: "3-6 hours",
      availability: "24/7",
      icon: "Settings",
      description: "Critical network equipment malfunction affecting operations"
    },
    {
      id: 4,
      type: "Security Breach",
      responseTime: "1 hour",
      availability: "24/7",
      icon: "Shield",
      description: "Network security incidents requiring immediate attention"
    }
  ];

  const regionalContacts = [
    {
      region: "Northeast",
      phone: "(555) 911-1001",
      coverage: "NY, NJ, CT, MA, PA"
    },
    {
      region: "Southeast",
      phone: "(555) 911-1002",
      coverage: "FL, GA, SC, NC, VA"
    },
    {
      region: "Midwest",
      phone: "(555) 911-1003",
      coverage: "IL, IN, OH, MI, WI"
    },
    {
      region: "Southwest",
      phone: "(555) 911-1004",
      coverage: "TX, AZ, NM, OK, AR"
    },
    {
      region: "West Coast",
      phone: "(555) 911-1005",
      coverage: "CA, OR, WA, NV, UT"
    }
  ];

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl shadow-brand-moderate border-2 border-red-200 overflow-hidden">
      {/* Emergency Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Icon name="AlertTriangle" size={32} className="animate-pulse" />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
            </div>
            <div>
              <h3 className="text-2xl font-bold">24/7 Emergency Service</h3>
              <p className="opacity-90">Immediate response when you need it most</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm opacity-90">Current Time</div>
            <div className="text-xl font-mono font-bold">{formatTime(currentTime)}</div>
          </div>
        </div>
      </div>
      <div className="p-6">
        {/* Emergency Hotline */}
        <div className="bg-white rounded-lg p-6 mb-6 border-2 border-red-200 shadow-brand-subtle">
          <div className="text-center">
            <Icon name="Phone" size={48} className="text-red-600 mx-auto mb-3" />
            <h4 className="text-2xl font-bold text-text-primary mb-2">Emergency Hotline</h4>
            <div className="text-4xl font-bold text-red-600 mb-3 font-mono">1-800-FIBER-911</div>
            <p className="text-text-secondary mb-4">Available 24/7/365 for critical network emergencies</p>
            <Button
              variant="destructive"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              className="animate-pulse"
            >
              Call Emergency Line
            </Button>
          </div>
        </div>

        {/* Emergency Service Types */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
            <Icon name="Clock" size={20} className="mr-2" />
            Emergency Response Services
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {emergencyServices?.map((service) => (
              <div key={service?.id} className="bg-white rounded-lg p-4 border border-red-200">
                <div className="flex items-start space-x-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <Icon name={service?.icon} size={20} className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-text-primary">{service?.type}</h5>
                    <p className="text-sm text-text-secondary mb-2">{service?.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                        Response: {service?.responseTime}
                      </span>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                        {service?.availability}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Regional Emergency Contacts */}
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
            <Icon name="MapPin" size={20} className="mr-2" />
            Regional Emergency Contacts
          </h4>
          <div className="bg-white rounded-lg border border-red-200 overflow-hidden">
            {regionalContacts?.map((contact, index) => (
              <div key={contact?.region} className={`p-4 ${index !== regionalContacts?.length - 1 ? 'border-b border-red-100' : ''}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-semibold text-text-primary">{contact?.region} Region</h5>
                    <p className="text-sm text-text-secondary">{contact?.coverage}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-red-600">{contact?.phone}</div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Phone"
                      iconPosition="left"
                      className="mt-1"
                    >
                      Call Now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Preparation Tips */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-text-primary mb-3 flex items-center">
            <Icon name="Info" size={20} className="mr-2 text-yellow-600" />
            Before You Call
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <h5 className="font-medium text-text-primary mb-2">Have Ready:</h5>
              <ul className="space-y-1 text-text-secondary">
                <li>• Account/Service ID number</li>
                <li>• Location of the issue</li>
                <li>• Description of the problem</li>
                <li>• Contact information</li>
              </ul>
            </div>
            <div>
              <h5 className="font-medium text-text-primary mb-2">Safety First:</h5>
              <ul className="space-y-1 text-text-secondary">
                <li>• Stay away from damaged cables</li>
                <li>• Don't attempt repairs yourself</li>
                <li>• Secure the area if possible</li>
                <li>• Document with photos if safe</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmergencyServiceCard;